import { useState } from "react";
import SectionTitle from "./SectionTitle";

type Archive = {
  id: string;
  name: string;
  desc: string;
  count: string;
  updated: string;
  hot: boolean;
};

const CATALOG: Record<string, Archive[]> = {
  现场视频: [
    { id: "V-01", name: "街口突发多机位", desc: "两处视角画面已对齐时间戳", count: "2 段", updated: "今日", hot: true },
    { id: "V-02", name: "侧向补拍镜头", desc: "正面之外的角度，细节更近全貌", count: "1 段", updated: "昨日", hot: false },
    { id: "V-03", name: "移动端随手实拍", desc: "画质参差，按场景单独归档", count: "6 段", updated: "本周", hot: false },
  ],
  实拍图证: [
    { id: "P-11", name: "环境方位核对", desc: "多条街口实拍拼出方位关系", count: "12 张", updated: "今日", hot: true },
    { id: "P-12", name: "流出场景补全", desc: "光线与地标对应还原现场", count: "7 张", updated: "昨日", hot: false },
    { id: "P-13", name: "细节放大对表", desc: "把模糊处局部放大交叉比照", count: "9 张", updated: "本周", hot: false },
  ],
  截图线索: [
    { id: "C-21", name: "时间戳排序重做", desc: "一张截图改变了先后判断", count: "4 条", updated: "昨日", hot: true },
    { id: "C-22", name: "对话口径出入", desc: "关键说法差异已列清单", count: "2 条", updated: "今晨", hot: true },
    { id: "C-23", name: "转发链路追踪", desc: "谁先发布、几经转发可查", count: "5 条", updated: "本周", hot: false },
  ],
  目击者说: [
    { id: "W-31", name: "先到者的叙述", desc: "第一位目击者先后时间记录", count: "1 人", updated: "今晨", hot: false },
    { id: "W-32", name: "口径分歧专题", desc: "在场人数与顺序说法不一", count: "3 人", updated: "今日", hot: true },
    { id: "W-33", name: "后续跟进补证", desc: "有人进一步补述当时所见", count: "2 人", updated: "本周", hot: false },
  ],
};

const TABS = Object.keys(CATALOG);

export default function IndexTabs() {
  const [tab, setTab] = useState<string>("现场视频");
  const list = CATALOG[tab] ?? [];

  return (
    <section id="index" className="scroll-mt-20 border-y border-foreground-200/70 bg-secondary-50/70 py-16 md:py-24">
      <div className="mx-auto max-w-6xl px-4 md:px-6">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12 lg:items-end lg:gap-8">
          <div className="lg:col-span-8">
            <SectionTitle
              index="03"
              kicker="话题索引"
              title={<>把散落的材料，收进一本持续更新的档案</>}
              lead="四类索引按需切换：视频归到机位与时间，图证归到方位与细节，截图归到链路，目击归到说法。目录大字显示，方便直接按类检索。"
            />
          </div>
          <div className="lg:col-span-4 lg:pb-10">
            <div className="flex items-center justify-between rounded-lg border border-foreground-200 bg-background-50 px-5 py-4">
              <div>
                <p className="text-[12px] text-foreground-500">累计归档档案</p>
                <p className="font-serif text-3xl font-black text-primary-600">1,847</p>
              </div>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary-200/80 px-3 py-1 text-[11px] text-secondary-700">
                <i className="ri-time-line text-[13px]" aria-hidden="true" />持续更新
              </span>
            </div>
          </div>
        </div>

        {/* segmented control */}
        <div className="mb-8 flex flex-wrap gap-2">
          <div className="inline-flex rounded-full border border-foreground-200 bg-background-50 p-1">
            {TABS.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTab(t)}
                className={`rounded-full px-4 py-2 text-sm transition-colors whitespace-nowrap ${
                  tab === t
                    ? "bg-foreground-950 text-background-50"
                    : "text-foreground-600 hover:text-foreground-950"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
          <span className="self-center text-[12px] text-foreground-400">每栏独立编号，方便回引</span>
        </div>

        {/* catalog */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-5">
          {list.map((a) => (
            <a
              key={a.id}
              href="#discuss"
              className="group relative flex flex-col justify-between overflow-hidden rounded-lg border border-foreground-200/70 bg-background-50 p-5 transition-colors hover:border-primary-300 md:p-6"
            >
              {a.hot && (
                <span className="absolute right-4 top-4 inline-flex items-center gap-1 rounded-full bg-primary-100 px-2.5 py-1 text-[10px] font-medium text-primary-700">
                  <span className="inline-flex h-1.5 w-1.5 rounded-full bg-primary-500 animate-pulse" />更新中
                </span>
              )}
              <div>
                <div className="flex items-center gap-3">
                  <span className="font-mono text-[12px] tracking-wide text-foreground-400">{a.id}</span>
                  <span className="h-px flex-1 bg-foreground-200/80" />
                </div>
                <h3 className="mt-4 font-serif text-lg font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600">
                  {a.name}
                </h3>
                <p className="mt-2 text-[13px] leading-6 text-foreground-500">{a.desc}</p>
              </div>
              <div className="mt-5 flex items-center justify-between border-t border-foreground-200/60 pt-4">
                <span className="font-serif text-xl font-bold text-primary-600">{a.count}</span>
                <span className="inline-flex items-center gap-2 text-[12px] text-foreground-400">
                  <i className="ri-time-line text-[13px]" aria-hidden="true" />{a.updated}更新
                  <i className="ri-arrow-right-line text-[13px] transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
                </span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}